import React, { useEffect, useState } from 'react';
import { Card, STitle, BottomNav, Spinner } from './ui';
import { C, CIVS } from '../data/constants';
import { getLevel } from '../data/player';
import { fetchGameHistory } from '../lib/db';
import { isConfigured } from '../lib/supabase';

export default function Profile({ onTab, activeTab, player, user, onSignIn, onSignOut }) {
  const [remoteHistory, setRemoteHistory] = useState(null);
  const [signOutBusy,   setSignOutBusy]   = useState(false);

  const p   = player || {};
  const lvl = getLevel(p.totalScore || 0);

  // Fetch remote game history when logged in
  useEffect(() => {
    if (!user || !isConfigured) return;
    fetchGameHistory(user.id, 10)
      .then(h => setRemoteHistory(h))
      .catch(() => {});
  }, [user]);

  const stats = [
    ['✅', p.totalCorrect  || 0, 'Correct'],
    ['🎯', p.gamesPlayed   || 0, 'Rounds'],
    ['🔥', p.bestStreak    || 0, 'Streak'],
    ['⭐', p.totalScore    || 0, 'Score'],
  ];

  const mastery = CIVS.filter(c => p.civStats?.[c.key]?.rounds > 0).map(c => {
    const cs = p.civStats[c.key];
    return { ic: c.icon, name: c.label, pct: Math.round((cs.bestPct || 0) * 100), color: c.border };
  });

  const displayName = user
    ? (p.username || user.email?.split('@')[0] || 'Scholar')
    : (p.username || 'Anonymous Scholar');
  const initials = displayName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  // Merge local history + remote history
  const localHistory   = p.history || [];
  const displayHistory = remoteHistory
    ? remoteHistory.map(h => ({
        score: h.score, correct: h.correct, total: h.total,
        civ: h.civ, diff: h.diff, date: new Date(h.created_at).getTime(),
      }))
    : localHistory;

  async function handleSignOut() {
    setSignOutBusy(true);
    await onSignOut?.();
    setSignOutBusy(false);
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(180deg,#FFF9E8,#fff)', padding: '20px 16px', borderBottom: `2px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ position: 'relative', width: 70, margin: '0 auto 12px' }}>
          <div style={{ width: 70, height: 70, borderRadius: '50%', background: 'linear-gradient(135deg,#FFE080,#FFC020)', border: `3px solid ${C.amber}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: p.username ? 22 : 30, fontWeight: 900, color: C.text }}>
            {p.username ? initials : '𓂀'}
          </div>
          <div style={{ position: 'absolute', bottom: -4, right: -4, background: user ? C.teal : C.amber, color: '#fff', borderRadius: 20, padding: '2px 7px', fontSize: 9, fontWeight: 700, border: '2px solid #fff' }}>
            {user ? '🔗 Synced' : '📜 Local'}
          </div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 900, textAlign: 'center', marginBottom: 2 }}>{displayName}</div>
        <div style={{ fontSize: 11, color: C.muted, textAlign: 'center', marginBottom: 12 }}>{lvl.title}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 5 }}>
          {stats.map(([ic, n, l]) => (
            <Card key={l} style={{ padding: 8, textAlign: 'center' }}>
              <div style={{ fontSize: 13, marginBottom: 1 }}>{ic}</div>
              <div style={{ fontSize: 16, fontWeight: 900 }}>{n}</div>
              <div style={{ fontSize: 8, color: C.faint, fontWeight: 700, textTransform: 'uppercase' }}>{l}</div>
            </Card>
          ))}
        </div>
      </div>

      <div className="scroll-area" style={{ padding: '14px 16px' }}>
        {/* Sign-in / sign-out */}
        {isConfigured && !user && (
          <div style={{ background: '#FFF9E8', border: `2px solid ${C.goldLight}`, borderRadius: 14, padding: 14, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Sync your progress</div>
              <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>Sign in to access your data across all devices and unlock live duels.</div>
            </div>
            <button onClick={onSignIn}
              style={{ padding: '8px 14px', background: C.amber, color: '#fff', border: 'none', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Roboto, sans-serif', flexShrink: 0 }}>
              Sign In
            </button>
          </div>
        )}

        {isConfigured && user && (
          <div style={{ background: '#E8F8F0', border: `2px solid #90DEB8`, borderRadius: 14, padding: '10px 14px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.teal }}>🔗 Account synced</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{user.email}</div>
            </div>
            <button onClick={handleSignOut} disabled={signOutBusy}
              style={{ padding: '6px 12px', background: '#fff', color: C.muted, border: `2px solid ${C.border}`, borderRadius: 9, fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: 'Roboto, sans-serif' }}>
              {signOutBusy ? '…' : 'Sign Out'}
            </button>
          </div>
        )}

        {/* Duel wins badge */}
        {(p.duelWins || 0) > 0 && (
          <Card style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontSize: 22 }}>⚔️</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700 }}>Duel Wins</div>
              <div style={{ fontSize: 11, color: C.muted }}>Win 3 to earn the Lion of Aksum title</div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: C.amber }}>{p.duelWins}</div>
          </Card>
        )}

        {/* XP progress bar */}
        <Card style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 700, lineHeight: 1.3 }}>{lvl.title}</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.teal }}>{lvl.pct}%</div>
          </div>
          <div style={{ height: 10, background: '#F0E8D8', borderRadius: 5, overflow: 'hidden', border: '2px solid #E0D0C0' }}>
            <div style={{ height: '100%', width: `${lvl.pct}%`, background: `linear-gradient(90deg,${C.teal},${C.teal2})`, borderRadius: 3, transition: 'width .6s' }} />
          </div>
          {lvl.next && (
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              {lvl.xpLeft} pts to <strong>{lvl.next}</strong>
            </div>
          )}
        </Card>

        {/* Civilization Mastery */}
        <STitle>Civilization Mastery</STitle>
        {mastery.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: C.muted, fontSize: 13 }}>
            🌍 Play rounds to track your mastery!
          </div>
        ) : (
          mastery.map(m => (
            <div key={m.name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 0', borderBottom: `2px solid ${C.border}` }}>
              <div style={{ fontSize: 18, width: 26, textAlign: 'center' }}>{m.ic}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{m.name}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.muted }}>{m.pct}%</div>
                </div>
                <div style={{ height: 8, background: '#F0E8D8', borderRadius: 4, overflow: 'hidden', border: '1px solid #E0D0C0' }}>
                  <div style={{ height: '100%', width: `${m.pct}%`, background: m.color, borderRadius: 3 }} />
                </div>
              </div>
            </div>
          ))
        )}

        {/* Recent Games */}
        {user && isConfigured && remoteHistory === null && (
          <div style={{ textAlign: 'center', padding: '12px 0', color: C.muted, fontSize: 12 }}>
            <Spinner /> Loading game history…
          </div>
        )}

        {displayHistory.length > 0 && (
          <>
            <STitle style={{ marginTop: 16 }}>
              Recent Games {remoteHistory ? '🔗' : ''}
            </STitle>
            {displayHistory.slice(0, 8).map((h, i) => {
              const civMeta = CIVS.find(c => c.key === h.civ);
              const pct     = h.total > 0 ? Math.round((h.correct / h.total) * 100) : 0;
              const dateStr = new Date(h.date).toLocaleDateString();
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: `2px solid ${C.border}` }}>
                  <div style={{ fontSize: 20 }}>{civMeta?.icon || '🌍'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>{civMeta?.label || 'All Civs'} · {h.diff}</div>
                    <div style={{ fontSize: 10, color: C.muted }}>{dateStr}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 900, color: C.amber }}>+{h.score}</div>
                    <div style={{ fontSize: 10, color: pct >= 70 ? C.green : C.muted, fontWeight: 700 }}>{h.correct}/{h.total} ({pct}%)</div>
                  </div>
                </div>
              );
            })}
          </>
        )}

        <div style={{ height: 16 }} />
      </div>

      <BottomNav active={activeTab} onTab={onTab} />
    </div>
  );
}
